export const SERVICES = [
  {
    slug: "vce-english-tutoring-melbourne",
    nav: "VCE English Tutoring",
    title: "VCE English Tutoring in Melbourne",
    metaTitle: "VCE English Tutoring Melbourne | Small Group Classes",
    metaDescription:
      "VCE English tutoring in Melbourne, taught in small groups by tutors who scored a raw 50. Text response, argument analysis and oral — with marked essays every week.",
    h1: "VCE English tutoring in Melbourne",
    intro:
      "English is the one subject every VCE student has to sit, and the one where marks move the most once a student is taught how the study design actually rewards writing. Our classes are English-only, capped small, and run by tutors who have sat the exam recently and scored at the very top of the state.",
    sections: [
      {
        h2: "What we actually cover",
        body: "Every Units 3–4 assessment task gets its own block of the term: analytical text response, comparative response where your school runs it, argument and language analysis, and the oral presentation. We work from the same texts your school has set, so nothing you do with us is a side project — it is the work you are being graded on, done properly and marked before it counts.",
        list: [
          "Analytical text response — thesis, structure, embedded evidence and metalanguage",
          "Argument and language analysis — how to write about how a contention is built, not just spot devices",
          "Oral presentation — topic selection, statement of intention and delivery",
          "Exam conditions practice with real VCAA-style prompts and timed writing",
        ],
      },
      {
        h2: "Why small groups beat one-on-one for English",
        body: "English improves fastest when a student reads other people's writing and argues about interpretation out loud. In a group of a handful of students you hear four other readings of the same passage, you defend your own, and you see what a stronger paragraph looks like next to yours. You also get the accountability of a weekly deadline. That is why we cap our classes instead of running lecture halls, and why we do not sell one-on-one as the default.",
      },
      {
        h2: "Marked work every single week",
        body: "Students write, we mark, and the feedback comes back specific enough to act on before the next piece. Over a term that becomes a folder of graded essays with a visible trajectory — which is exactly what a student needs going into the exam, and exactly what most tutoring never produces.",
      },
      {
        h2: "Who teaches it",
        body: "Yahav founded Tutors Melbourne after scoring a raw 50 in VCE English and a 99+ ATAR. Every tutor on the team has sat English recently and scored at the top of the state, so the advice a student gets is current with the study design, not a decade out of date.",
      },
    ],
    faqs: [
      { q: "Are classes online or in person?", a: "Both — we run a hybrid model, so you can join online or come in person around Melbourne, whichever suits the family." },
      { q: "Do you follow my school's texts?", a: "Yes. We teach the texts your school has set and mark the tasks you are actually being assessed on." },
      { q: "How big are the groups?", a: "Deliberately small — small enough that every student's writing gets read and discussed every week." },
      { q: "What year levels do you take?", a: "Year 7 through Year 12, with most of our demand in Units 3–4 English and EAL." },
    ],
  },
  {
    slug: "vce-english-small-group-classes",
    nav: "Small Group Classes",
    title: "Small Group English Classes",
    metaTitle: "Small Group VCE English Classes Melbourne | Year 7–12",
    metaDescription:
      "Small group English classes for Year 7–12 in Melbourne. Capped group sizes, weekly marked essays, hybrid online or in-person. Taught by raw 50 English tutors.",
    h1: "Small group English classes for Year 7–12",
    intro:
      "Our whole model is the small group. Not a private tutor charging by the hour, and not a lecture hall of sixty students watching slides — a capped group where the tutor knows every student's writing well enough to name what is holding each of them back.",
    sections: [
      {
        h2: "What a class actually looks like",
        body: "Each week has a shape: we open with the concept, work a passage or prompt together, then students write while the tutor moves between them. Work goes home, comes back marked, and the next class opens with what the group got wrong. Nobody sits at the back quietly not understanding — in a group this size there is nowhere to hide, which is the point.",
        list: [
          "One focused skill per week rather than a scattergun revision session",
          "Live writing time with the tutor reading over your shoulder",
          "Comprehensive notes so the student is not frantically transcribing",
          "24/7 Ask Anything tutor chat between classes",
        ],
      },
      {
        h2: "Why not one-on-one?",
        body: "One-on-one has its place, but for English it removes the single most useful thing available to a student: other people's readings of the same text. Discussion is where interpretation sharpens. A small group keeps that debate while still giving each student individual feedback on their writing — and it costs a family considerably less per class than private tutoring.",
      },
      {
        h2: "Limited spots, on purpose",
        body: "We cap group sizes because the model stops working the moment a tutor cannot get through everyone's writing in a week. That means places genuinely run out each term rather than as a sales line, and it is worth enrolling before a term starts rather than mid-way through.",
      },
    ],
    faqs: [
      { q: "How small is small?", a: "Small enough that every student's work is read and discussed weekly — we cap each group rather than filling seats." },
      { q: "Can we try a class first?", a: "Get in touch through the enrolment form. We talk to every family on the phone before placing a student, so we can work out the right group first." },
      { q: "What if we miss a week?", a: "Credits keep working and there is year-round access to lesson recordings on our term packages, so a missed week is not a lost week." },
    ],
  },
  {
    slug: "eal-tutoring-melbourne",
    nav: "EAL Tutoring",
    title: "VCE EAL Tutoring in Melbourne",
    metaTitle: "VCE EAL Tutoring Melbourne | Small Group EAL Classes",
    metaDescription:
      "VCE EAL tutoring in Melbourne. Small group classes covering listening, text response and analysis for EAL students, with weekly marked writing and hybrid delivery.",
    h1: "VCE EAL tutoring in Melbourne",
    intro:
      "EAL is not mainstream English with easier marking. It is a different study design with its own listening task, its own note-taking demands and its own assessor expectations — and it is badly served by generic English tutoring. We teach it as its own subject.",
    sections: [
      {
        h2: "The parts of EAL that decide the score",
        body: "Most EAL students lose marks in predictable places: the listening component, precision of expression under time pressure, and writing about how language works in a text when English is not the language they think in. Those are trainable, and they are what we drill.",
        list: [
          "Listening task — note-taking structure, question types and timing",
          "Text response and analysis with EAL assessor expectations in mind",
          "Precision and idiom — writing that reads naturally rather than translated",
          "Vocabulary building tied to the set texts, not generic word lists",
        ],
      },
      {
        h2: "Patient teaching, high expectations",
        body: "Students in our EAL groups are held to the same standard of argument as any other student — the support is in how the teaching is delivered, not in lowering the bar. Small groups matter even more here, because students need to speak, be corrected kindly, and hear the language used well by people their own age.",
      },
      {
        h2: "For parents",
        body: "If English is not the language spoken at home, a lot of the usual support a parent gives — reading a draft, checking an interpretation — is genuinely hard to provide. That is precisely the gap we fill: marked work every week, notes the student can revisit, and a tutor available in chat between classes.",
      },
    ],
    faqs: [
      { q: "Do you run EAL separately from mainstream English?", a: "Yes. EAL has its own study design and its own listening task, so it is taught with EAL requirements front and centre." },
      { q: "Is the listening task covered?", a: "Yes — note-taking method, question types and timed practice are part of the course." },
      { q: "My child is eligible for EAL but nervous about speaking. Is that a problem?", a: "Not at all. Groups are small and deliberately supportive; speaking builds up over the term rather than being demanded on day one." },
    ],
  },
  {
    slug: "year-11-english-tutoring",
    nav: "Year 11 English",
    title: "Year 11 English Tutoring",
    metaTitle: "Year 11 English Tutoring Melbourne | Units 1–2 Small Groups",
    metaDescription:
      "Year 11 English tutoring in Melbourne. Small group classes that build the analytical writing habits Units 3–4 assumes, with marked essays every week.",
    h1: "Year 11 English tutoring in Melbourne",
    intro:
      "Year 11 is the cheapest year to fix a student's writing. Units 1–2 marks do not follow them into their ATAR, but the habits do — and Units 3–4 opens by assuming a student can already build an argument, embed evidence and write to time. Most cannot yet, because nobody taught them explicitly.",
    sections: [
      {
        h2: "What we build in Year 11",
        body: "We use the year to install the mechanics: what a thesis actually is, how a paragraph earns its place, how to quote without padding, and how to plan an essay in five minutes instead of twenty. By the time a student walks into Units 3–4, the argument is the only new thing they have to think about.",
        list: [
          "Thesis writing and essay structure that holds up under exam time pressure",
          "Analysing set texts closely rather than retelling plot",
          "Language analysis foundations — how persuasion is constructed",
          "Writing to time, every week, with real feedback",
        ],
      },
      {
        h2: "Students doing a 3–4 subject early",
        body: "If your child is taking a Units 3–4 subject in Year 11, they are sitting an exam a year ahead of their peers with a year less writing practice. That is a solvable problem, but not one to leave until August — talk to us early so they are placed in the right group.",
      },
      {
        h2: "The quiet advantage",
        body: "Students who arrive in Year 12 already able to write are calmer, faster, and spend the year deepening ideas instead of learning structure from scratch. It is the least dramatic and most reliable way to protect an ATAR.",
      },
    ],
    faqs: [
      { q: "Is Year 11 too early for tutoring?", a: "It is arguably the best time. Units 1–2 marks do not count toward the ATAR, so it is the safest year to rebuild how a student writes." },
      { q: "Do you cover our school's Year 11 texts?", a: "Yes — we teach the texts your school has set." },
      { q: "Can they continue with the same group into Year 12?", a: "That is the intention. Continuity is a large part of why our students improve." },
    ],
  },
  {
    slug: "year-12-english-tutoring",
    nav: "Year 12 English",
    title: "Year 12 English Tutoring",
    metaTitle: "Year 12 English Tutoring Melbourne | Units 3–4 Small Groups",
    metaDescription:
      "Year 12 English tutoring in Melbourne. Units 3–4 text response, argument analysis and oral, taught in small groups with weekly marked essays and exam practice.",
    h1: "Year 12 English tutoring in Melbourne",
    intro:
      "Year 12 English is a writing exam, and writing improves through marked repetition — not through re-reading notes. Our Units 3–4 groups are built around that single fact: students write every week, work is marked properly, and the exam is practised under exam conditions long before November.",
    sections: [
      {
        h2: "The three tasks, taught deliberately",
        body: "Text response, argument and language analysis, and the oral each have their own logic and their own traps. We teach them as separate crafts rather than as generic essay writing, and we align the term with the order your school assesses them.",
        list: [
          "Analytical text response on your school's set texts",
          "Argument and language analysis on current, real media",
          "Oral presentation — contention, statement of intention, delivery",
          "Full timed practice exams marked against the criteria",
        ],
      },
      {
        h2: "Exam technique is a skill, not a personality trait",
        body: "Running out of time, blanking on a prompt, writing a strong second essay and a weak first — these are technique problems with technique fixes. Planning under pressure, prompt decoding in five minutes, and knowing what to sacrifice when time runs short are all taught and drilled.",
      },
      {
        h2: "Starting late in the year",
        body: "If it is already mid-year, the honest answer is that there is still meaningful ground to make up — argument analysis and exam technique in particular move quickly. Places are capped though, so the sooner you enrol the more likely we can place your child in the right group.",
      },
    ],
    faqs: [
      { q: "Is it too late to start in Term 3 or 4?", a: "No — analysis skills and exam technique respond fast. Spots are limited though, so enrol as early as you can." },
      { q: "Do you mark practice exams?", a: "Yes, against the assessment criteria, with feedback specific enough to change the next attempt." },
      { q: "Do you teach the comparative text task?", a: "Where your school runs it, yes — we teach the tasks you are actually being assessed on." },
    ],
  },
];

export const findService = (slug) => SERVICES.find((s) => s.slug === slug);
